import * as React from "react";
import { merge, $ } from "glamor";
import moment from "moment";
import { theme } from "styles/theme";
import { INotificationStatusParams, INotification } from "reducers/notifications";
import { Link } from "react-router";
import { Markdown } from "react-showdown";
import classNames from "classnames";
import Badge from "material-ui/Badge";

interface IProps {
    activity: INotification;
    index: number;
    onUpdateUserNotifications: (params: INotificationStatusParams) => void;
    merchantId?: string;
}

const Content: React.FunctionComponent<{ markup: string; createdAt: string; currentTime: string }> = ({
    markup,
    createdAt,
    currentTime,
}) => {
    return (
        <React.Fragment>
            <Markdown markup={markup} />
            <div className="subtitleContainer">
                <div className="subtitle" title={new Date(createdAt).toLocaleString()}>
                    {currentTime}
                </div>
            </div>
        </React.Fragment>
    );
};

export const UserActivity: React.FunctionComponent<IProps> = ({
    activity,
    index,
    onUpdateUserNotifications,
    merchantId,
}) => {
    const styles = React.useMemo(() => {
        return merge(
            {
                display: "flex",
                width: "100%",
            },
            $(" .subtitleContainer", {
                margin: "0 8px 8px 8px",
            }),
            $(" .subtitle", {
                lineHeight: "20px",
                fontSize: "12px",
                color: theme.palette.darkGrey,
            }),
            $(" .summary", {
                margin: "0 8px 8px 8px",
                fontSize: "14px",
                color: theme.palette.darkGrey,
                overflow: "auto",
                wordBreak: "break-word",
            }),
            $(" p", {
                fontSize: "14px",
            }),
            $(" strong", {
                fontWeight: "bold",
            }),
            $(" em", {
                fontStyle: "italic",
            }),
            $(" a", {
                color: theme.palette.darkGrey,
            }),
            $(" .logo", {
                minWidth: "56px !important",
                padding: "12px 0 0 0 !important",
            }),
            $(" .logoImage", {
                height: "44px",
                width: "44px",
                borderRadius: "50%",
                border: "1px #e1e1e1 solid",
            }),
            $(" .linkContainer", {
                padding: "12px 12px 0px 12px",
                wordBreak: "normal",
            }),
            $(" .unseenEntry", {
                height: "10px",
                width: "10px",
                borderRadius: "50%",
                backgroundColor: theme.palette.accent1Color,
                position: "absolute",
                left: "20px",
            })
        );
    }, []);

    const placeholderURL = "//cdn1.nuorder.com/brands/1womenswholesale_missing.jpg";
    const badgeStyle = React.useMemo(() => {
        const active = {
            backgroundColor: theme.palette.primary1Color,
            left: "45px",
            top: "25px",
            height: "10px",
            width: "10px",
        };
        return {
            active,
            inactive: {
                ...active,
                display: "none",
            },
        };
    }, []);
    const currentTime = React.useMemo(() => {
        return moment(activity.created_at).fromNow();
    }, [activity.created_at]);

    const { body_markdown, body, logo_url, url } = activity;
    const displayText = body_markdown || body;

    const handlingUpdateUserNotifications = (notificationIndex: number): void => {
        onUpdateUserNotifications({ read: [notificationIndex], merchant_id: merchantId });
    };

    return (
        <div {...styles} className={classNames({ unreadEntry: !activity.is_read })}>
            <div className="logo">
                <Badge badgeContent="" badgeStyle={activity.is_seen ? badgeStyle.inactive : badgeStyle.active}>
                    <img
                        className="logoImage"
                        src={logo_url && logo_url.href ? logo_url.href : placeholderURL}
                        alt=""
                    />
                </Badge>
            </div>
            {url && url.href ? (
                <Link
                    to={url.href}
                    target="_blank"
                    className="linkContainer"
                    onClick={handlingUpdateUserNotifications.bind(this, index)}
                >
                    <Content markup={displayText} currentTime={currentTime} createdAt={activity.created_at} />
                </Link>
            ) : (
                <div className="linkContainer">
                    <Content markup={displayText} currentTime={currentTime} createdAt={activity.created_at} />
                </div>
            )}
        </div>
    );
};
